import React from 'react';
import Head from 'next/head';
import NextSeo from 'next-seo';
import { NextPage, NextPageContext } from 'next';

import { Image } from 'shared-types/Common';
import { Property } from 'shared-types/Property';

import { useTranslations } from '@lang';
import { Products as ProductsComponent } from '@features/products/pages/Products';
import * as entityActions from '@store/entity/actions';
import { NormalizedStore } from '@store/entity/entity.types';
import { ComingSoonBanner } from '@common/components/ComingSoonBanner';

type ProductsProps = {
  id?: string | string[];
  language: string;
  propertyTitle: string;
  socialFallbackImage: Image;
  url: string;
  isComingSoonEnabled: boolean;
};

const Products: NextPage<ProductsProps> = ({
  id,
  language,
  propertyTitle,
  socialFallbackImage,
  url,
  isComingSoonEnabled,
}) => {
  const { t } = useTranslations();
  const pageName = t('app.products');

  const pageTitle = propertyTitle ? `${pageName} | ${propertyTitle}` : pageName;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: socialFallbackImage && socialFallbackImage.url }],
          },
        }}
      />
      {isComingSoonEnabled && <ComingSoonBanner />}
      <ProductsComponent id={id} />
    </>
  );
};

type ProductsInitialProps = {
  language: string;
  property: Property;
  store: NormalizedStore;
  url: string;
  isComingSoonEnabled: boolean;
} & NextPageContext;

Products.getInitialProps = async ({
  store,
  property,
  language,
  query,
  url,
}: ProductsInitialProps): Promise<ProductsProps> => {
  await entityActions.fetchProducts(property.id, language)(store.dispatch);

  return {
    id: query.id,
    propertyTitle: property.propertyTitle,
    isComingSoonEnabled: property.isComingSoonEnabled,
    socialFallbackImage: property.socialFallbackImage,
    language,
    url,
  };
};

export default Products;
